<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Search, X } from 'lucide-vue-next';
import { useClients } from '../composables/useClients';

const router = useRouter();
const { t } = useI18n();
const { clients, isLoading, error, currentPage, hasMore, fetchClients, clearClients } =
  useClients();

const searchQuery = ref('');
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

watch(searchQuery, (val) => {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    clients.value = [];
    fetchClients(1, 10, val);
  }, 300);
});

const clearSearch = () => {
  searchQuery.value = '';
};

const handleLoadMore = () => {
  if (hasMore.value && !isLoading.value) {
    fetchClients(currentPage.value + 1);
  }
};

const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

onMounted(() => {
  clearClients();
  fetchClients(1);
});
</script>

<template>
  <div class="flex flex-col min-h-full bg-[#0A0A0B]">
    <div class="flex-1 p-4 md:p-6 lg:p-8">
      <div class="max-w-7xl mx-auto">
        <!-- Page Header -->
        <div class="mb-6">
          <h2 class="text-2xl font-bold text-[#F5F0EA]">
            {{ t('clients.title') }}
          </h2>
          <p class="text-[#524840] mt-1">{{ t('clients.subtitle') }}</p>
        </div>

        <!-- Search -->
        <div class="mb-4 relative">
          <Search
            class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#524840] pointer-events-none"
          />
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="t('clients.search_placeholder')"
            class="w-full pl-9 pr-9 py-2.5 bg-[#111113] border border-[#2A2018] rounded-xl text-sm text-[#F5F0EA] placeholder-[#9E9080] focus:outline-none focus:ring-2 focus:ring-teal-400 transition"
          />
          <button
            v-if="searchQuery"
            @click="clearSearch"
            class="absolute right-3 top-1/2 -translate-y-1/2 text-[#524840] hover:text-[#F5F0EA] transition"
          >
            <X class="w-4 h-4" />
          </button>
        </div>

        <!-- Error -->
        <div
          v-if="error"
          class="mb-6 p-4 bg-red-900/20 border border-red-800 rounded-xl text-red-400"
        >
          {{ error }}
        </div>

        <!-- Table container -->
        <div class="bg-[#111113] rounded-xl border border-[#2A2018] overflow-hidden">
          <!-- Desktop Table -->
          <div class="hidden md:block overflow-x-auto">
            <table class="w-full">
              <thead class="bg-[#1E1E23] border-b border-[#2A2018]">
                <tr>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-[#524840] uppercase tracking-wider"
                  >
                    {{ t('clients.name') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-[#524840] uppercase tracking-wider"
                  >
                    {{ t('clients.phone') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-[#524840] uppercase tracking-wider"
                  >
                    {{ t('clients.email') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-[#524840] uppercase tracking-wider"
                  >
                    {{ t('clients.confirmed') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-[#524840] uppercase tracking-wider"
                  >
                    {{ t('clients.source') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-[#524840] uppercase tracking-wider"
                  >
                    {{ t('clients.created_at') }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-[#2A2018]">
                <tr v-if="isLoading && currentPage === 1">
                  <td colspan="6" class="px-6 py-12 text-center text-[#524840]">
                    {{ t('clients.loading') }}
                  </td>
                </tr>
                <tr v-else-if="clients.length === 0">
                  <td colspan="6" class="px-6 py-12 text-center text-[#524840]">
                    {{ t('clients.empty') }}
                  </td>
                </tr>
                <tr
                  v-for="client in clients"
                  :key="client.id"
                  @click="router.push(`/clients/${client.id}`)"
                  class="hover:bg-[#1E1E23] transition cursor-pointer"
                >
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="flex items-center gap-3">
                      <div
                        class="w-8 h-8 bg-teal-900/30 rounded-full flex items-center justify-center flex-shrink-0"
                      >
                        <span class="text-teal-300 text-xs font-medium">
                          {{ client.name[0]?.toUpperCase() }}
                        </span>
                      </div>
                      <span class="text-sm font-medium text-[#F5F0EA]">{{ client.name }}</span>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-[#524840]">
                    {{ client.data.phone_number || t('clients.no_phone') }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-[#524840]">
                    {{ client.data.email || t('clients.no_email') }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      :class="[
                        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                        client.confirmed
                          ? 'bg-green-900/30 text-green-400'
                          : 'bg-[#1E1E23] text-[#524840]',
                      ]"
                    >
                      {{
                        client.confirmed ? t('clients.confirmed_yes') : t('clients.confirmed_no')
                      }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      v-if="client.source"
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-900/30 text-blue-400"
                    >
                      {{ client.source.name }}
                    </span>
                    <span v-else class="text-sm text-[#9E9080]">—</span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-[#524840]">
                    {{ formatDate(client.created_at) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Mobile Cards -->
          <div class="md:hidden divide-y divide-[#2A2018]">
            <div v-if="isLoading && currentPage === 1" class="p-6 text-center text-[#524840]">
              {{ t('clients.loading') }}
            </div>
            <div v-else-if="clients.length === 0" class="p-6 text-center text-[#524840]">
              {{ t('clients.empty') }}
            </div>
            <div
              v-for="client in clients"
              :key="client.id"
              @click="router.push(`/clients/${client.id}`)"
              class="p-4 hover:bg-[#1E1E23] transition cursor-pointer"
            >
              <div class="flex items-start gap-3">
                <div
                  class="w-10 h-10 bg-teal-900/30 rounded-full flex items-center justify-center flex-shrink-0"
                >
                  <span class="text-teal-300 text-sm font-medium">
                    {{ client.name[0]?.toUpperCase() }}
                  </span>
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center justify-between gap-2">
                    <h3 class="text-sm font-medium text-[#F5F0EA] truncate">
                      {{ client.name }}
                    </h3>
                    <span
                      :class="[
                        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0',
                        client.confirmed
                          ? 'bg-green-900/30 text-green-400'
                          : 'bg-[#1E1E23] text-[#524840]',
                      ]"
                    >
                      {{
                        client.confirmed ? t('clients.confirmed_yes') : t('clients.confirmed_no')
                      }}
                    </span>
                  </div>
                  <p v-if="client.data.phone_number" class="text-sm text-[#524840] mt-0.5">
                    {{ client.data.phone_number }}
                  </p>
                  <p v-if="client.data.email" class="text-sm text-[#524840]">
                    {{ client.data.email }}
                  </p>
                  <p class="text-xs text-[#9E9080] mt-1">
                    {{ formatDate(client.created_at) }}
                  </p>
                  <span
                    v-if="client.source"
                    class="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-900/30 text-blue-400"
                  >
                    {{ client.source.name }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Load More -->
          <div v-if="hasMore && clients.length > 0" class="border-t border-[#2A2018] p-4">
            <button
              @click="handleLoadMore"
              :disabled="isLoading"
              class="w-full py-2.5 px-4 bg-[#1E1E23] hover:bg-[#2A2018] disabled:bg-[#111113] disabled:cursor-not-allowed text-[#9E9080] rounded-xl transition font-medium"
            >
              {{ isLoading ? t('clients.loading_more') : t('clients.load_more') }}
            </button>
          </div>

          <!-- No more -->
          <div
            v-if="!hasMore && clients.length > 0"
            class="border-t border-[#2A2018] p-4 text-center text-sm text-[#9E9080]"
          >
            {{ t('clients.no_more') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
